import { useState, useEffect, useRef } from 'react';
import { BUILD_HASH, API_URL } from '../env-config';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const SESSION_KEY = 'su_update_suppressed';

interface VersionCheckResult {
  isUpdateAvailable: boolean;
  latestVersion: string | null;
  dismiss: () => void;
}

export function useVersionCheck(): VersionCheckResult {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const latestVersionRef = useRef<string | null>(null);

  const dismiss = () => {
    try {
      if (latestVersionRef.current) {
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ suppressedVersion: latestVersionRef.current })
        );
      }
    } catch {
      // sessionStorage unavailable or full — dismiss state not persisted but UI still clears
    }
    setIsUpdateAvailable(false);
  };

  useEffect(() => {
    // Local dev: BUILD_HASH is 'dev', skip all polling
    if (BUILD_HASH === 'dev') return;

    const checkVersion = async () => {
      try {
        const res = await fetch(`${API_URL}/health`);
        if (!res.ok) return;
        const data = await res.json();
        const apiVersion: string = data?.version;
        if (!apiVersion || apiVersion === 'unknown') return;
        if (apiVersion === BUILD_HASH) return;

        // Check sessionStorage suppression
        try {
          const suppressed = sessionStorage.getItem(SESSION_KEY);
          if (suppressed) {
            const { suppressedVersion } = JSON.parse(suppressed);
            if (suppressedVersion === apiVersion) return;
          }
        } catch {
          // sessionStorage unavailable — continue
        }

        latestVersionRef.current = apiVersion;
        setLatestVersion(apiVersion);
        setIsUpdateAvailable(true);
      } catch {
        // Network error — swallow silently, polling continues
      }
    };

    checkVersion(); // Run immediately on mount
    const interval = setInterval(checkVersion, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []); // No deps — BUILD_HASH and API_URL are module-level constants

  return { isUpdateAvailable, latestVersion, dismiss };
}
